import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAvailableEpcs, useAssignEpc, useUnassignEpc } from '@/api/hooks/useEpcs';
import { assetKeys } from '@/api/hooks/useAssets';
import { useQueryClient } from '@tanstack/react-query';
import { Radio, Search, Loader2, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChangeEpcDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string | number;
  currentEpc?: string;
  assetName: string;
}

export function ChangeEpcDialog({
  open,
  onOpenChange,
  assetId,
  currentEpc,
  assetName,
}: ChangeEpcDialogProps) {
  const { t } = useTranslation(['assets', 'common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEpc, setSelectedEpc] = useState<string | null>(null);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  const queryClient = useQueryClient();
  const { data: availableEpcs, isLoading } = useAvailableEpcs();
  const assignEpc = useAssignEpc();
  const unassignEpc = useUnassignEpc();

  const filteredEpcs = useMemo(() => {
    if (!availableEpcs) return [];
    if (!searchTerm.trim()) return availableEpcs;
    const term = searchTerm.toLowerCase();
    return availableEpcs.filter(epc =>
      epc.code.toLowerCase().includes(term)
    );
  }, [availableEpcs, searchTerm]);

  const handleAssignEpc = async () => {
    if (!selectedEpc) return;

    try {
      // If there's a current EPC, unlink it first
      if (currentEpc) {
        await unassignEpc.mutateAsync(currentEpc);
      }

      // Assign the new EPC
      await assignEpc.mutateAsync({
        epcTag: selectedEpc,
        assetId: String(assetId),
      });

      // Invalidate asset query to refresh detail page
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(assetId) });

      // Reset and close
      setSelectedEpc(null);
      setSearchTerm('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error assigning EPC:', error);
    }
  };

  const handleUnlinkEpc = async () => {
    if (!currentEpc) return;

    try {
      await unassignEpc.mutateAsync(currentEpc);

      // Invalidate asset query to refresh detail page
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(assetId) });

      // Reset and close
      setShowUnlinkConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error unlinking EPC:', error);
    }
  };

  const handleClose = () => {
    setSelectedEpc(null);
    setSearchTerm('');
    setShowUnlinkConfirm(false);
    onOpenChange(false);
  };

  const isProcessing = assignEpc.isPending || unassignEpc.isPending;

  if (showUnlinkConfirm) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('epcDialog.unlinkTitle')}</DialogTitle>
            <DialogDescription>
              {t('epcDialog.unlinkConfirm', { epc: currentEpc, assetName }).split('<strong>').map((part, i) => {
                if (i === 0) return part;
                const [bold, rest] = part.split('</strong>');
                return <span key={i}><strong className={i === 1 ? "font-mono" : ""}>{bold}</strong>{rest}</span>;
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowUnlinkConfirm(false)}
              disabled={isProcessing}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlinkEpc}
              disabled={isProcessing}
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('epcDialog.unlinkButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            {t('epcDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {currentEpc
              ? t('epcDialog.currentEpcDescription', { epc: currentEpc, assetName })
              : t('epcDialog.noCurrentEpcDescription', { assetName })
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('epcDialog.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* EPC List */}
          <ScrollArea className="h-[250px] rounded-md border">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredEpcs.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Radio className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchTerm
                    ? t('epcDialog.noResults')
                    : t('epcDialog.noAvailable')
                  }
                </p>
              </div>
            ) : (
              <div className="p-2">
                {filteredEpcs.map((epc) => (
                  <button
                    key={epc.id}
                    type="button"
                    onClick={() => setSelectedEpc(epc.code)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors',
                      selectedEpc === epc.code
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    )}
                  >
                    <span className="font-mono">{epc.code}</span>
                    {selectedEpc === epc.code && (
                      <Check className="h-4 w-4" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Current EPC unlink option */}
          {currentEpc && (
            <div className="flex items-center justify-between rounded-md border border-dashed p-3">
              <div className="text-sm">
                <span className="text-muted-foreground">{t('epcDialog.currentEpcLabel')} </span>
                <span className="font-mono">{currentEpc}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUnlinkConfirm(true)}
                className="text-destructive hover:text-destructive"
              >
                <X className="mr-1 h-4 w-4" />
                {t('epcDialog.unlinkButton')}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleAssignEpc}
            disabled={!selectedEpc || isProcessing}
          >
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('epcDialog.assignButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
